import React from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  AsyncSelect,
  CheckForm,
  Divider,
  Form,
  Grid,
  Group,
  InlineAlert,
  Input,
  Modal,
  Select,
  TextArea,
} from "@samskip/frontend-components";
import { Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { nameof } from "ts-simple-nameof";

import { LabelConstants, BookingTemplateConstants } from "@assets/constants";
import { BookingTemplateFormTypes } from "@assets/formTypes";
import {
  defaultPartnerForm,
  PartnerForm,
} from "@assets/formTypes/bookingTemplates";
import { BookingTemplateTypes, PortTypes, CommonTypes } from "@assets/types";
import { getErrorMessage } from "@assets/utilities";
import {
  cityCountryLabelMapper,
  cityValueMapper,
  partnerAddressHeaderLabelMapper,
  partnerAddressLabelMapper,
  partnerAddressValueMapper,
} from "@assets/utilities/labelMapperUtils";
import { BookingTemplatesYup } from "@assets/yupObjects";
import { WithFormController } from "@components";
import PortService from "@services/PortService";

interface PartnerModalProps {
  addressType: BookingTemplateConstants.PARTNER_ADDRESS_TYPE;
  formValues?: PartnerForm;
  isLoading: boolean;
  onClose: CommonTypes.CallbackFunction;
  onSave: (
    modalType: BookingTemplateConstants.PARTNER_ADDRESS_TYPE,
    values: BookingTemplateFormTypes.PartnerForm
  ) => void;
  partners: BookingTemplateTypes.Partner[];
  title?: string;
}
const PartnerModal = ({
  addressType,
  formValues = defaultPartnerForm,
  isLoading,
  onClose,
  onSave,
  partners,
  title = "",
}: PartnerModalProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: formValues,
    resolver: yupResolver(BookingTemplatesYup.partnerValidationSchema),
    mode: "onTouched",
  });
  const partnerFillOutType = watch("PartnerFillOutType");

  const onPartnerChange = (partner: BookingTemplateTypes.Partner) => {
    /* For some reason the PartnerFillOutType sometimes is set to null when selecting a partner;
     To avoid this, as a quick fix set it always to previous when selecting a partner. */
    setValue(
      "PartnerFillOutType",
      BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.PREVIOUS
    );

    const fieldsToPopulate = [
      nameof<PartnerForm>((i) => i.CompanyName),
      nameof<PartnerForm>((i) => i.StreetAndNumber),
      nameof<PartnerForm>((i) => i.City),
      nameof<PartnerForm>((i) => i.PostalCode),
      nameof<PartnerForm>((i) => i.County),
      nameof<PartnerForm>((i) => i.Country),
      nameof<PartnerForm>((i) => i.AdditionalAddressInfo),
      nameof<PartnerForm>((i) => i.ContactName),
      nameof<PartnerForm>((i) => i.Email),
      nameof<PartnerForm>((i) => i.Phone),
    ];

    fieldsToPopulate.forEach((fieldName: Path<PartnerForm>) =>
      setValue(fieldName, partner[fieldName] ?? defaultPartnerForm[fieldName])
    );

    if (partner.PointCode) {
      PortService.getPort(partner.PointCode).then((port: PortTypes.Port) => {
        setValue("CityPort", port);
      });
    }
  };

  const searchCity = (searchString: string): Promise<PortTypes.Port[]> => {
    return PortService.searchPorts(searchString);
  };

  const onAddressTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    reset({
      ...defaultPartnerForm,
      PartnerFillOutType: e.target
        .value as BookingTemplateConstants.PARTNER_FILL_OUT_TYPE,
    });
  };

  // When the "Previous partner" option is selected and the partner is not yet selected, the inputs and selects need to be disabled
  const isFieldDisabled =
    partnerFillOutType ===
    BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.PREVIOUS
      ? !watch("PartnerCompany")
      : false;

  const onSubmit = (data: PartnerForm) => {
    onSave(addressType, data);
    onClose();
  };

  const getPartnerInfoAlert = (): string => {
    switch (partnerFillOutType) {
      case BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.PREVIOUS:
        return t("INFO_PARTNER_CHANGE_TEMPLATE");
      case BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.BOOKINGPARTY:
        return t("INFO_BOOKING_PARTY_PARTNER_CHANGE_TEMPLATE");
      case BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.NEW:
        return t("INFO_NEW_PARTNER_CHANGE_TEMPLATE");
    }
  };

  return (
    <Modal
      closeIcon
      corner="sharp"
      divided
      onClose={onClose}
      open
      primaryButtonProps={{
        children: t("LABEL_SAVE"),
        form: "partner_form",
        type: "submit",
      }}
      secondaryButtonProps={{ children: t("LABEL_CANCEL"), onClick: onClose }}
      title={title}
    >
      <Form
        gap="small"
        id="partner_form"
        method="post"
        onSubmit={handleSubmit(onSubmit)}
      >
        {partnerFillOutType !==
          BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.BOOKINGPARTY && (
          <>
            <Group onChange={onAddressTypeChange}>
              <CheckForm
                bordered
                id="partner_previous"
                label={t("LABEL_PREVIOUS_PARTNER")}
                type="radio"
                value={BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.PREVIOUS}
                {...register("PartnerFillOutType")}
              />

              <CheckForm
                bordered
                id="partner_new"
                label={t("LABEL_NEW_PARTNER")}
                type="radio"
                value={BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.NEW}
                {...register("PartnerFillOutType")}
              />
            </Group>
            <Divider />
          </>
        )}
        {!isFieldDisabled && <InlineAlert>{getPartnerInfoAlert()}</InlineAlert>}
        {partnerFillOutType ===
          BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.PREVIOUS && (
          <WithFormController control={control} name="PartnerCompany">
            <Select
              errorMessage={getErrorMessage(
                nameof<PartnerForm>((i) => i.PartnerCompany),
                errors
              )}
              fullWidth
              getHeaderSubheaderLabel={partnerAddressHeaderLabelMapper}
              getOptionLabel={partnerAddressLabelMapper}
              getOptionValue={partnerAddressValueMapper}
              hasError={
                errors[nameof<PartnerForm>((i) => i.PartnerCompany)] != null
              }
              id="partner-company"
              isLoading={isLoading}
              label={t("LABEL_PARTNER")}
              onChange={onPartnerChange}
              options={partners}
              placeholder={t("PLACEHOLDER_SELECT_PARTNER")}
            />
          </WithFormController>
        )}
        <Input
          disabled={
            partnerFillOutType ===
              BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.PREVIOUS ||
            partnerFillOutType ===
              BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.BOOKINGPARTY
          }
          disabledTooltipContent={
            !getValues("CompanyName") ? t("ERROR_SELECT_PARTNER_FIRST") : null
          }
          errorMessage={getErrorMessage(
            nameof<PartnerForm>((i) => i.CompanyName),
            errors
          )}
          fullWidth
          hasError={errors[nameof<PartnerForm>((i) => i.CompanyName)] != null}
          id="company_name"
          label={t("LABEL_COMPANY_NAME")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_COMPANY_NAME")}
          {...register("CompanyName")}
        />
        <Input
          disabled={isFieldDisabled}
          disabledTooltipContent={t("ERROR_SELECT_PARTNER_FIRST")}
          fullWidth
          id="company_street"
          label={t("LABEL_STREET_AND_NUMBER")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_STREET_AND_NUMBER")}
          {...register("StreetAndNumber")}
        />
        <Grid mdCol={2}>
          {partnerFillOutType ===
          BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.NEW ? (
            <WithFormController control={control} name="CityPort">
              <AsyncSelect
                defaultOptionsMessage={t(
                  LabelConstants.SELECT_LABELS.DEFAULT_OPTIONS_MESSAGE
                )}
                errorMessage={getErrorMessage(
                  nameof<PartnerForm>((i) => i.CityPort),
                  errors
                )}
                fullWidth
                getOptionLabel={cityCountryLabelMapper}
                getOptionValue={cityValueMapper}
                hasError={
                  errors[nameof<PartnerForm>((i) => i.CityPort)] != null
                }
                id="company_city"
                isClearable
                label={t("LABEL_TOWN_CITY")}
                loadOptions={searchCity}
                noOptionsMessage={t(LabelConstants.SELECT_LABELS.NO_OPTIONS)}
                placeholder={t("PLACEHOLDER_TOWN_CITY")}
                required
              />
            </WithFormController>
          ) : (
            <Input
              disabled={isFieldDisabled}
              disabledTooltipContent={t("ERROR_SELECT_PARTNER_FIRST")}
              fullWidth
              id="company_city"
              label={t("LABEL_TOWN_CITY")}
              placeholder={t("PLACEHOLDER_TOWN_CITY")}
              {...register("City")}
            />
          )}
          <Input
            disabled={isFieldDisabled}
            disabledTooltipContent={t("ERROR_SELECT_PARTNER_FIRST")}
            fullWidth
            id="company_postal_code"
            label={t("LABEL_POSTAL_CODE")}
            maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_POSTAL_CODE}
            placeholder={t("PLACEHOLDER_POSTAL_CODE")}
            {...register("PostalCode")}
          />
        </Grid>
        <Input
          disabled={isFieldDisabled}
          disabledTooltipContent={t("ERROR_SELECT_PARTNER_FIRST")}
          fullWidth
          id="company_district"
          label={`${t("LABEL_DISTRICT")} / ${t("LABEL_COUNTY")} / ${t(
            "LABEL_POSTBOX"
          )}`}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_DISTRICT_COUNTY_POSTBOX")}
          {...register("County")}
        />
        <Input
          disabled={isFieldDisabled}
          disabledTooltipContent={t("ERROR_SELECT_PARTNER_FIRST")}
          fullWidth
          id="company_country"
          label={t("LABEL_COUNTRY")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_COUNTRY")}
          {...register("Country")}
        />
        <Input
          disabled={isFieldDisabled}
          disabledTooltipContent={t("ERROR_SELECT_PARTNER_FIRST")}
          fullWidth
          id="company_additional_INFO"
          label={t("LABEL_ADDITIONAL_ADDRESS_INFO")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_ADDITIONAL_ADDRESS_INFO")}
          {...register("AdditionalAddressInfo")}
        />
        <Divider />
        <Input
          disabled={isFieldDisabled}
          disabledTooltipContent={t("ERROR_SELECT_PARTNER_FIRST")}
          fullWidth
          id="company_contact_person"
          label={t("LABEL_CONTACT_PERSON_NAME")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_GENERIC}
          placeholder={t("PLACEHOLDER_CONTACT_PERSON_NAME")}
          {...register("ContactName")}
        />

        <TextArea
          disabled={isFieldDisabled}
          disabledTooltipContent={t("ERROR_SELECT_PARTNER_FIRST")}
          errorMessage={getErrorMessage(
            nameof<PartnerForm>((i) => i.Email),
            errors
          )}
          formatMaxCharactersText={(count: number) =>
            t("TEXT_N_CHARACTERS_LEFT", { count: count })
          }
          fullWidth
          hasError={errors[nameof<PartnerForm>((i) => i.Email)] != null}
          helperMessage={t("INFO_MULTIPLE_EMAILS")}
          id="company_email"
          key={
            watch("PartnerCompany")
              ? watch("PartnerCompany")?.PartnerCode
              : "partner-email"
          } // Needed to rerender the component every time the default email length might change to show the left chars correctly (it's because react-hook-form is working with the uncontrolled value)
          label={t("LABEL_EMAIL")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_EMAIL}
          nonResizable
          placeholder={t("PLACEHOLDER_EMAIL_MULTIPLE")}
          rows={2}
          uncontrolledValue={watch("Email") ? watch("Email") : ""}
          {...register("Email")}
        />
        <Input
          disabled={isFieldDisabled}
          disabledTooltipContent={t("ERROR_SELECT_PARTNER_FIRST")}
          errorMessage={getErrorMessage(
            nameof<PartnerForm>((i) => i.Phone),
            errors
          )}
          fullWidth
          hasError={errors[nameof<PartnerForm>((i) => i.Phone)] != null}
          id="company_phone"
          label={t("LABEL_PHONE")}
          maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_PHONE}
          placeholder={t("PLACEHOLDER_PHONE")}
          {...register("Phone")}
        />
      </Form>
    </Modal>
  );
};

export default PartnerModal;
